/* globals window */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useMatchWidth } from '@birdi/shared-deprecated/match-width';
import { useLocation, useNavigate } from 'react-router';
import { assistant as assistantAPI } from '@birdi/js-sdk';
import { openSupportChat } from '@birdi/intercom';
import AiLoop from '@birdi/icons/ai-loop-colour.svg';
import ScoutAvatar from '@birdi/icons/ScoutAvatar.svg';
import SendGradient from '@birdi/icons/send-gradient.svg';
import ArrowLeft from '@birdi/icons/arrow-left.svg';
import ArrowRight from '@birdi/icons/arrow-right.svg';
import Close from '@birdi/icons/close.svg';
import { BetaBadge, IconButton } from '@birdi/components-common';
import { useMetaSelector } from '../meta/reducer';
import {
  useAssistantSelector,
  openAssistant,
  minimizeAssistant,
  closeAssistant,
  clearPendingPrompt,
} from './reducer';
import { useDispatch } from 'react-redux';
import { usePostHog } from 'posthog-js/react';
import { useSnapshot } from 'valtio';
import {
  getMapSnapshot,
  runMapAction,
  assistantUi,
  mapLayout,
  registerAssistantOpener,
  notifyDataChanged,
  setAssistantWidth,
  isAssistantHiddenPath,
  ASSISTANT_WIDTH_DEFAULT,
} from './map-bridge';
import { AssistantSideTab } from './AssistantLauncher';
import {
  useAutoGrow,
  EMPTY_STATE_COMPOSER_BOUNDS,
  FOLLOW_UP_COMPOSER_BOUNDS,
} from './use-auto-grow';
import { Markdown } from './Markdown';

/**
 * Default width of the docked side column. The LIVE width is
 * `assistantUi.width` (user-resizable, persisted) — read that, not this, so the
 * panel and the space RootLayout reserves for it can't disagree. Kept exported
 * because other call sites still reference the default.
 */
export const ASSISTANT_PANEL_WIDTH = ASSISTANT_WIDTH_DEFAULT;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Human labels of tools the assistant ran while producing this message. */
  tools: string[];
  /** Quick-reply options offered with this answer. They stay in the
   * transcript after the turn moves on, greyed out, so the user can see
   * what was offered — and which one they picked. */
  suggestions?: string[];
  selectedSuggestion?: string;
}

type AgentEvent =
  | { type: 'text'; delta: string }
  | { type: 'tool'; label: string }
  | { type: 'navigate'; to: string }
  | {
      type: 'map_action';
      action:
        | 'set_visibility'
        | 'focus_layer'
        | 'switch_view'
        | 'set_units'
        | 'deselect_all'
        | 'search_layers'
        | 'fit_view'
        | 'open_request_processing'
        | 'export_annotation_report'
        | 'open_upload_modal'
        | 'show_timeline'
        | 'open_map_export'
        | 'show_table_view'
        | 'start_annotation'
        | 'start_volume_measurement'
        | 'set_basemap'
        | 'configure_split_compare'
        | 'open_difference_grid'
        | 'add_library_layer'
        | 'start_ai_detect'
        | 'capture_map_view'
        | 'move_layers'
        | 'rename_layer';
      captureToken?: string;
      layerId?: string;
      layerIds?: string[];
      visible?: boolean;
      bbox?: [number, number, number, number];
      view?: '2d' | '3d' | 'media' | 'split';
      system?: 'metric' | 'imperial';
      term?: string;
      tool?: 'Marker' | 'Polygon' | 'Line' | 'Circle' | 'Rectangle';
      surface?: 'map_area' | 'orthomosaic' | 'media';
      style?:
        | 'light'
        | 'dark'
        | 'satellite'
        | 'satellite-hybrid'
        | 'outdoor'
        | 'map'
        | 'white'
        | 'black';
      leftLayerId?: string | null;
      rightLayerId?: string | null;
      libraryLayerId?: string;
      parentId?: string | null;
      newFolderName?: string;
      position?: 'top' | 'bottom';
      aboveLayerId?: string;
      belowLayerId?: string;
      name?: string;
    }
  | { type: 'data_changed'; scope: 'maps' | 'map_folders' }
  | { type: 'open_support_chat'; prefill?: string }
  | { type: 'suggestions'; options: string[] }
  | { type: 'done' }
  | { type: 'error'; message: string };

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const MAP_PATH_RE = /^\/map\/next\/([0-9a-fA-F-]{36})/;

const CHAT_STORAGE_KEY = 'birdi-assistant-chat';

/**
 * Ops model choice, remembered per tab. Testing means asking the same thing on
 * two models and comparing, and a reload mid-comparison that silently reverted
 * to the default would make the second half of the test a lie.
 */
const MODEL_STORAGE_KEY = 'birdi-assistant-ops-model';

// Empty-state "Try Birdi's AI Agent for..." shortcuts; clicking sends the label.
const PANEL_SUGGESTIONS = [
  'Process my imagery',
  'Measure a volume',
  'Detect objects in this map',
  'Generate an annotation report',
  'Share map',
  // Last: not a workflow, but the entry point users otherwise never find.
  // Sending the bare label is enough — the agent asks what went wrong, then
  // confirms before filing anything (report_bug never fires unprompted).
  'Report a bug',
];
const LAYER_CAP = 40;

/**
 * Status lines are plain strings (they persist to sessionStorage, so the shape
 * stays flat). A run of the same tool is collapsed into one line carrying a
 * "×N" suffix; these two read and write that suffix.
 */
const REPEAT_SUFFIX_RE = /\s×(\d+)$/;
export const stripRepeatCount = (label: string): string =>
  label.replace(REPEAT_SUFFIX_RE, '');
export const repeatCountOf = (label: string): number => {
  const m = REPEAT_SUFFIX_RE.exec(label);
  return m ? Number(m[1]) : 1;
};

/**
 * App context sent with each turn: where the user is, plus — when a map is
 * open — its live viewport, layers, and selection read from the map bridge.
 */
const buildContext = (pathname: string) => {
  const mapMatch = MAP_PATH_RE.exec(pathname);
  const ctx: Record<string, unknown> = {
    path: pathname,
    ...(mapMatch ? { mapId: mapMatch[1] } : {}),
    // Whether contact_support can actually hand off from this browser. Sent
    // so the AGENT knows before it writes: Intercom is absent in local dev
    // and wherever INTERCOM_ENABLED is off, and a model that assumed it was
    // there would tell the user a chat had opened when none had.
    supportChatAvailable:
      typeof (window as any).Intercom === 'function',
  };
  const snap = getMapSnapshot();
  if (snap && snap.mapId) {
    ctx.mapId = snap.mapId;
    ctx.map = {
      // Forwarded so the agent can tell "this map is empty" apart from "its
      // layers haven't hydrated yet" — while loading, layerCount is 0 and the
      // layer arrays are empty, which otherwise reads as a genuinely empty map.
      loading: snap.loading === true,
      bounds: snap.bounds,
      center: snap.center,
      zoom: snap.zoom,
      renderer: snap.renderer,
      layerCount: snap.layerCount,
      selectedLayerIds: snap.selectedLayerIds.slice(0, 20),
      visibleLayers: snap.visibleLayers.slice(0, LAYER_CAP),
      hiddenLayers: snap.hiddenLayers.slice(0, LAYER_CAP),
    };
  }
  return ctx;
};

// Friendly labels for the composer context chip on non-map pages.
const PAGE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/sites': 'Sites',
  '/missions/mine': 'My Maps',
};

/**
 * Label for the composer context chip, grounding the chat in where the user
 * is acting: "Map: <name>" when a map is open (name read from the bridge,
 * polled briefly while the map hydrates), a page label on known pages, else
 * null (chip hidden).
 */
function useContextChipLabel(
  pathname: string,
  active: boolean,
): string | null {
  const { mapMounted } = useSnapshot(mapLayout);
  const [mapName, setMapName] = useState<string | null>(null);
  const onMap = MAP_PATH_RE.test(pathname) || mapMounted;
  useEffect(() => {
    if (!active || !onMap) {
      setMapName(null);
      return undefined;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let tries = 0;
    const read = () => {
      if (cancelled) return;
      const snap = getMapSnapshot();
      if (snap?.mapName) setMapName(snap.mapName);
      // The name can hydrate a beat after mount — poll briefly until the
      // map reports loaded.
      if ((!snap || snap.loading || !snap.mapName) && tries < 40) {
        tries += 1;
        timer = setTimeout(read, 500);
      }
    };
    read();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, onMap, pathname]);
  if (onMap) return mapName ? `Map: ${mapName}` : null;
  const page = PAGE_LABELS[pathname];
  return page ? `Page: ${page}` : null;
}

/** The dashed "where am I acting" context chip in the composer. */
const ContextChip: React.FC<{ label: string }> = ({ label }) => (
  <span
    data-testid="assistant-context-chip"
    css={(theme: BirdiTheme) => css`
      max-width: 75%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.8em;
      color: ${theme.mono60};
      border: 1.5px dashed color-mix(in srgb, ${theme.ocean} 45%, transparent);
      border-radius: 8px;
      padding: 3px 8px;
    `}
  >
    {label}
  </span>
);

/**
 * Standing reminder that the answers are model output. Sits under the composer
 * so it is present while the user is deciding what to send, not just after a
 * reply lands — the point is to set expectations, not to caveat one answer.
 */
export const AiDisclaimer: React.FC = () => (
  <p
    data-testid="assistant-ai-disclaimer"
    css={(theme: BirdiTheme) => css`
      margin: 0;
      text-align: center;
      font-size: 0.75em;
      line-height: 1.35;
      color: ${theme.mono50};
    `}
  >
    AI can make mistakes — please double-check responses
  </p>
);

export const AssistantWidget: React.FC = () => {
  const meta = useMetaSelector();
  const { isOpen, isMinimized, pendingPrompt } = useAssistantSelector();
  // Live docked width (user-resizable, persisted) + the in-drag flag.
  const panelWidth = useSnapshot(assistantUi).width;
  const [resizing, setResizing] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Conversation survives page reloads within the tab (sessionStorage).
  // Live tab state restores from sessionStorage; past conversations live
  // server-side (assistant_chats) and are reopened via the Recent chats list.
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) return parsed as ChatMessage[]; // legacy shape
      if (parsed && Array.isArray(parsed.messages)) {
        return parsed.messages as ChatMessage[];
      }
    } catch {
      // Corrupt/unavailable storage — start fresh.
    }
    return [];
  });
  const [conversationId, setConversationId] = useState<string | null>(() => {
    try {
      const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed.conversationId === 'string') {
        return parsed.conversationId;
      }
    } catch {
      // fall through
    }
    return null;
  });
  const conversationIdRef = useRef<string | null>(conversationId);
  conversationIdRef.current = conversationId;
  /** Recent server-side conversations shown on the home screen. */
  const [recentChats, setRecentChats] = useState<
    { id: string; title: string }[]
  >([]);
  /** Home screen shows 6 recents; "View more" reveals the rest. */
  const [recentsExpanded, setRecentsExpanded] = useState(false);
  const [input, setInput] = useState('');
  /**
   * BIR-277: both composers auto-grow to 8 rows, then scroll internally. The
   * resting heights stay as designed — 3 rows in the empty state, where the
   * long placeholder needs the room, 1 row in the follow-up, where the
   * transcript does — but the growth is the same hook either side of the
   * first message, so conversation length no longer changes how the box
   * behaves. Only one of the two is mounted at a time.
   */
  const emptyStateInputRef = useAutoGrow(input, EMPTY_STATE_COMPOSER_BOUNDS);
  const followUpInputRef = useAutoGrow(input, FOLLOW_UP_COMPOSER_BOUNDS);
  /**
   * Ops-only model switcher. The list comes from the API (empty for non-ops),
   * so this component never decides who is ops — it just renders what it is
   * given. Empty list => no control.
   */
  const [opsModels, setOpsModels] = useState<
    { id: string; label: string }[]
  >([]);
  const [opsModel, setOpsModel] = useState<string>(
    () => window.sessionStorage?.getItem(MODEL_STORAGE_KEY) || '',
  );
  const [streaming, setStreaming] = useState(false);
  // The assistant is desktop/tablet-only; on phones nothing renders at all.
  const isMobile = useMatchWidth(breakpoints.xs);

  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  messagesRef.current = messages;
  // Read at send time so each turn carries the page the user is on right now.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const opsModelRef = useRef(opsModel);
  opsModelRef.current = opsModel;

  // Ask once per mount which models this user may select. Non-ops gets [], so
  // the control never renders and no ops check happens in the client.
  useEffect(() => {
    let cancelled = false;
    assistantAPI
      .listModels()
      .then((r) => (r.ok ? r.json() : { models: [] }))
      .then((body) => {
        if (cancelled) return;
        const list = Array.isArray(body?.models) ? body.models : [];
        setOpsModels(list);
        // Drop a stored choice that is no longer selectable — otherwise every
        // turn 400s against a list that has moved on.
        setOpsModel((cur) =>
          cur && !list.some((m: { id: string }) => m.id === cur) ? '' : cur,
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const chooseOpsModel = useCallback((id: string) => {
    setOpsModel(id);
    try {
      if (id) window.sessionStorage?.setItem(MODEL_STORAGE_KEY, id);
      else window.sessionStorage?.removeItem(MODEL_STORAGE_KEY);
    } catch {
      // Private browsing — the choice just won't survive a reload.
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streaming, scrollToBottom]);

  const send = useCallback(
    async (text: string, opts?: { newConversation?: boolean }) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;
      const fresh = opts?.newConversation === true;

      // First message of a fresh chat mints the conversation id (groups the
      // turns server-side for Recent chats). Must be a real UUID — the server
      // validates — so skip grouping on ancient browsers without randomUUID.
      let convId = fresh ? null : conversationIdRef.current;
      if (!convId && typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        convId = crypto.randomUUID();
        setConversationId(convId);
      }

      const userMsg: ChatMessage = {
        id: newId(),
        role: 'user',
        content: trimmed,
        tools: [],
      };
      const assistantId = newId();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        tools: [],
      };

      // History sent to the server = the most recent turns + this user turn.
      // Trimmed well under the server's 40-message cap so long conversations
      // never hit a wall, and cut so it starts on a user message (the model
      // API requires user-first ordering). A fresh conversation starts empty.
      const base = fresh ? [] : messagesRef.current;
      const recent = [...base, userMsg].slice(-30);
      const firstUser = recent.findIndex((m) => m.role === 'user');
      const history = recent.slice(firstUser === -1 ? 0 : firstUser).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      setMessages((prev) =>
        fresh ? [userMsg, assistantMsg] : [...prev, userMsg, assistantMsg],
      );
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const patchAssistant = (fn: (m: ChatMessage) => ChatMessage) =>
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? fn(m) : m)),
        );

      // Set if an open_support_chat event couldn't be honoured; flushed as a
      // postscript on 'done' (see that case for why it can't be immediate).
      let supportChatFailed = false;

      try {
        const res = await assistantAPI.chatStream(history, {
          signal: controller.signal,
          context: buildContext(pathRef.current),
          conversationId: convId ?? undefined,
          // Read through a ref: a turn started before a switch must finish on
          // the model it began with, or the transcript attributes the answer to
          // the wrong one.
          model: opsModelRef.current || undefined,
        });
        if (res.status === 429) {
          patchAssistant((m) => ({
            ...m,
            content:
              "You're sending messages quickly — give it a minute and try again.",
          }));
          return;
        }
        if (!res.ok || !res.body) {
          throw new Error(`Request failed (${res.status})`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const handleEvent = (event: AgentEvent) => {
          switch (event.type) {
            case 'text':
              patchAssistant((m) => ({ ...m, content: m.content + event.delta }));
              break;
            case 'tool':
              patchAssistant((m) => {
                const tools = [...m.tools];
                const last = tools[tools.length - 1];
                // Collapse repeated wait_for_job progress pings into one line.
                if (
                  event.label.startsWith('Still waiting') &&
                  last?.startsWith('Still waiting')
                ) {
                  tools[tools.length - 1] = event.label;
                } else if (last && stripRepeatCount(last) === event.label) {
                  // Any tool run back-to-back collapses to one line with a
                  // count. Asking for 41 volumes used to print "Computing your
                  // volume…" 41 times, burying the actual answer — but hiding
                  // the repeats entirely would understate what is happening,
                  // so show how many.
                  tools[tools.length - 1] = `${event.label} ×${
                    repeatCountOf(last) + 1
                  }`;
                } else {
                  tools.push(event.label);
                }
                return { ...m, tools };
              });
              break;
            case 'done':
              if (supportChatFailed) {
                // Appended only once the model has finished writing, so the
                // correction reads as a postscript rather than contradicting
                // a "I've opened a chat" sentence streamed after it.
                supportChatFailed = false;
                patchAssistant((m) => ({
                  ...m,
                  content: `${m.content}\n\nI couldn't open the support chat here — email support@birdi.io and they'll pick it up.`,
                }));
              }
              if (!panelVisibleRef.current) assistantUi.unread = true;
              break;
            case 'navigate':
              navigate(event.to);
              break;
            case 'map_action':
              runMapAction((a) => {
                switch (event.action) {
                  case 'set_visibility':
                    if (event.layerId)
                      a.setLayerVisibility(event.layerId, event.visible ?? true);
                    break;
                  case 'focus_layer':
                    if (event.layerId) a.focusLayer(event.layerId, event.bbox);
                    break;
                  case 'switch_view':
                    if (event.view) a.switchView(event.view);
                    break;
                  case 'set_units':
                    if (event.system) a.setMeasurementSystem(event.system);
                    break;
                  case 'deselect_all':
                    a.deselectAll();
                    break;
                  case 'search_layers':
                    a.searchLayers(event.term ?? '');
                    break;
                  case 'fit_view':
                    if (event.bbox) a.fitView(event.bbox);
                    break;
                  case 'open_request_processing':
                    if (event.layerIds?.length)
                      a.openRequestProcessing(event.layerIds);
                    break;
                  case 'export_annotation_report':
                    if (event.layerIds?.length)
                      a.exportAnnotationReport(event.layerIds);
                    break;
                  case 'open_upload_modal':
                    a.openUploadModal();
                    break;
                  case 'show_timeline':
                    a.showTimeline();
                    break;
                  case 'open_map_export':
                    a.openMapExport();
                    break;
                  case 'show_table_view':
                    a.showTableView(event.layerIds);
                    break;
                  case 'start_annotation':
                    if (event.tool) a.startAnnotation(event.tool);
                    break;
                  case 'start_volume_measurement':
                    a.startVolumeMeasurement();
                    break;
                  case 'set_basemap':
                    if (event.style) a.setBasemap(event.style);
                    break;
                  case 'configure_split_compare':
                    a.configureSplitCompare(
                      event.leftLayerId ?? null,
                      event.rightLayerId ?? null,
                    );
                    break;
                  case 'open_difference_grid':
                    if (event.layerId) a.openDifferenceGrid(event.layerId);
                    break;
                  case 'add_library_layer':
                    if (event.libraryLayerId)
                      a.addLibraryLayer(event.libraryLayerId);
                    break;
                  case 'start_ai_detect':
                    if (event.surface)
                      a.startAiDetect(event.surface, event.layerIds);
                    break;
                  case 'capture_map_view':
                    if (event.captureToken)
                      a.captureMapView(event.captureToken);
                    break;
                  case 'move_layers':
                    if (event.layerIds?.length)
                      a.moveLayers(event.layerIds, {
                        parentId: event.parentId ?? null,
                        newFolderName: event.newFolderName,
                        position: event.position,
                        aboveLayerId: event.aboveLayerId,
                        belowLayerId: event.belowLayerId,
                      });
                    break;
                  case 'rename_layer':
                    if (event.layerId && event.name)
                      a.renameLayer(event.layerId, event.name);
                    break;
                  default:
                    break;
                }
              });
              break;
            case 'data_changed':
              notifyDataChanged(event.scope);
              break;
            case 'open_support_chat':
              // Opens Intercom with the draft pre-filled; the user sends it.
              // The agent already knows whether this is possible (context's
              // supportChatAvailable), so normally it succeeds. Only a race —
              // Intercom going away mid-turn — lands in the failure path, and
              // that correction is deferred to 'done' so it can't interleave
              // with text the model is still streaming.
              if (!openSupportChat(event.prefill)) supportChatFailed = true;
              break;
            case 'suggestions':
              if (Array.isArray(event.options)) {
                // 4 model options + a server-guaranteed "Create a new map".
                const options = event.options.slice(0, 5);
                patchAssistant((m) => ({ ...m, suggestions: options }));
              }
              break;
            case 'error':
              patchAssistant((m) => ({
                ...m,
                content: m.content || event.message,
              }));
              break;
            case 'done':
            default:
              break;
          }
        };

        // Read the SSE stream: events are separated by a blank line, payloads
        // are on `data:` lines.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            const line = part.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            const json = line.slice(line.indexOf(':') + 1).trim();
            if (!json) continue;
            try {
              handleEvent(JSON.parse(json) as AgentEvent);
            } catch {
              // ignore malformed chunk
            }
          }
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          patchAssistant((m) => ({
            ...m,
            content: m.content || '_Stopped._',
          }));
        } else {
          patchAssistant((m) => ({
            ...m,
            content:
              m.content ||
              'Sorry — I could not reach the assistant. Please try again.',
          }));
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [navigate, streaming],
  );

  // Consume a prompt handed off from the dashboard hero. Hero prompts (typed
  // or a Popular-questions pill) always START A NEW CHAT rather than appending
  // to whatever conversation was left open. If a stream is mid-flight, abort
  // it and let the effect re-fire once streaming settles.
  useEffect(() => {
    if (!isOpen || isMinimized || !pendingPrompt) return;
    if (streaming) {
      abortRef.current?.abort();
      return;
    }
    const prompt = pendingPrompt;
    dispatch(clearPendingPrompt());
    void send(prompt, { newConversation: true });
  }, [isOpen, isMinimized, pendingPrompt, streaming, dispatch, send]);

  // Routes the assistant is switched off on — the Sites pages (BIR-321). The
  // panel is HIDDEN there rather than closed, so an in-flight conversation is
  // still waiting when the user navigates back out.
  const suppressed = isAssistantHiddenPath(location.pathname);
  // The panel always docks as a right-hand column (RootLayout reserves the
  // matching space so it sits beside the app, not over it). On tablets it goes
  // full-width (see the panel's media query below); on phones the assistant
  // is disabled entirely.
  const panelVisible = isOpen && !isMinimized && !isMobile && !suppressed;
  // "Where am I acting" chip shown in the composer (map or page context).
  const contextChip = useContextChipLabel(location.pathname, panelVisible);

  // Analytics: one event per panel-open transition. Opening also clears the
  // launcher's unread dot.
  const posthog = usePostHog();
  const wasVisibleRef = useRef(false);
  useEffect(() => {
    if (panelVisible && !wasVisibleRef.current) {
      posthog?.capture('AI Assistant opened', { path: location.pathname });
    }
    if (panelVisible) assistantUi.unread = false;
    wasVisibleRef.current = panelVisible;
  }, [panelVisible, posthog, location.pathname]);
  const panelVisibleRef = useRef(panelVisible);
  panelVisibleRef.current = panelVisible;

  // Persist the live conversation for this tab (server holds the full record).
  useEffect(() => {
    try {
      sessionStorage.setItem(
        CHAT_STORAGE_KEY,
        JSON.stringify({ conversationId, messages: messages.slice(-40) }),
      );
    } catch {
      // Storage full/unavailable — persistence is best-effort.
    }
  }, [messages, conversationId]);

  // Load the Recent chats list whenever the user is on the home screen.
  useEffect(() => {
    if (!panelVisible || messages.length > 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await assistantAPI.listConversations();
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled && Array.isArray(body?.conversations)) {
          setRecentChats(
            body.conversations
              .slice(0, 30)
              .map((c: any) => ({ id: c.id, title: c.title })),
          );
        }
      } catch {
        // Recents are a nicety — fail silently.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [panelVisible, messages.length]);

  /** Reopen a past conversation from the server. */
  const openConversation = useCallback(async (id: string) => {
    try {
      const res = await assistantAPI.getConversation(id);
      if (!res.ok) return;
      const body = await res.json();
      if (!Array.isArray(body?.messages)) return;
      setMessages(
        body.messages.map((m: any) => ({
          id: newId(),
          role: m.role === 'user' ? 'user' : 'assistant',
          content: String(m.content ?? ''),
          tools: [],
        })),
      );
      setConversationId(id);
    } catch {
      // leave the home screen untouched on failure
    }
  }, []);

  /** Quick-reply pill clicked: remember the pick on its message, then send. */
  const chooseSuggestion = useCallback(
    (messageId: string, label: string) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, selectedSuggestion: label } : m,
        ),
      );
      void send(label);
    },
    [send],
  );

  /** Back to the home screen (the conversation stays reopenable). */
  const goHome = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationId(null);
    setInput('');
    try {
      sessionStorage.removeItem(CHAT_STORAGE_KEY);
    } catch {
      // best-effort
    }
  }, []);

  // Bridge the assistant's open state + opener to non-Redux consumers (the
  // launcher inside the map's separate store; see map-bridge.ts).
  useEffect(() => {
    registerAssistantOpener((prompt?: string) => dispatch(openAssistant(prompt)));
    return () => registerAssistantOpener(null);
  }, [dispatch]);
  useEffect(() => {
    assistantUi.open = panelVisible;
  }, [panelVisible]);

  // Plan gate: mirrored to the valtio bridge so Redux-free entry points (the
  // header launcher, incl. under the map's separate store) hide themselves.
  // The server enforces the same feature on every /v1/assistant route.
  const hasFeature = Boolean(meta.id) && meta.features.includes('ai-assistant');
  useEffect(() => {
    assistantUi.enabled = hasFeature;
  }, [hasFeature]);

  // Route gate, mirrored the same way: the header launcher and the side tab
  // are Redux- AND router-free, so they read this rather than the location.
  useEffect(() => {
    assistantUi.suppressed = suppressed;
  }, [suppressed]);

  // Only for authenticated users on a plan with the ai-assistant feature.
  if (!hasFeature) return null;

  const onSubmit = () => {
    const text = input;
    setInput('');
    void send(text);
  };

  return (
    <>
      {/* Right-edge "Ask Birdi's AI Agent" tab (self-hides while the panel is open). */}
      <AssistantSideTab />
      {panelVisible && (
        <div
          data-testid="assistant-panel"
          onKeyDown={(e) => {
            if (e.key === 'Escape') dispatch(minimizeAssistant());
          }}
          css={(theme: BirdiTheme) => css`
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            width: ${panelWidth}px;
            max-width: 100vw;
            z-index: 950;
            display: flex;
            flex-direction: column;
            background: ${theme.mono0};
            border-left: 1px solid ${theme.mono20};
            box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
            @media (max-width: ${breakpoints.s}) {
              width: 100vw;
            }
          `}
        >
          {/*
            Resize handle. 360px is one number for a 13" laptop and a 27"
            monitor, and on a map the panel comes straight off the canvas — so
            let people choose. Full-width on tablet, where the panel is the
            whole screen and there is nothing to resize against.
          */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the assistant panel"
            data-testid="assistant-resize-handle"
            onPointerDown={(e) => {
              // Pointer capture keeps the drag alive over the map canvas and
              // any iframe, which a plain mousemove listener loses.
              e.preventDefault();
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              setResizing(true);
            }}
            onPointerMove={(e) => {
              if (!resizing) return;
              // Docked on the right, so width grows as the pointer moves left.
              setAssistantWidth(window.innerWidth - e.clientX);
            }}
            onPointerUp={(e) => {
              (e.target as HTMLElement).releasePointerCapture(e.pointerId);
              setResizing(false);
            }}
            onDoubleClick={() => setAssistantWidth(ASSISTANT_WIDTH_DEFAULT)}
            onKeyDown={(e) => {
              // Keyboard-resizable, since a drag handle is otherwise unreachable.
              const step = e.shiftKey ? 48 : 16;
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setAssistantWidth(panelWidth + step);
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setAssistantWidth(panelWidth - step);
              } else if (e.key === 'Home') {
                e.preventDefault();
                setAssistantWidth(ASSISTANT_WIDTH_DEFAULT);
              }
            }}
            tabIndex={0}
            css={(theme: BirdiTheme) => css`
              position: absolute;
              left: -3px;
              top: 0;
              bottom: 0;
              width: 7px;
              cursor: col-resize;
              /* Above the panel's own content so the grab area is never
                 covered by the header or the transcript. */
              z-index: 1;
              touch-action: none;
              background: ${resizing ? theme.ocean : 'transparent'};
              transition: background 0.15s ease;
              &:hover,
              &:focus-visible {
                background: ${theme.ocean};
              }
              &:focus-visible {
                outline: none;
              }
              @media (max-width: ${breakpoints.s}) {
                display: none;
              }
            `}
          />
          {/* Header */}
          <div
            css={(theme: BirdiTheme) => css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 10px 14px;
              background: ${theme.lightOcean};
              border-bottom: 1px solid ${theme.mono20};
            `}
          >
            {messages.length > 0 ? (
              <div
                css={(theme: BirdiTheme) => css`
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  min-width: 0;
                  font-weight: 700;
                  color: ${theme.midnight};
                `}
              >
                <button
                  type="button"
                  aria-label="Back to recent chats"
                  data-testid="assistant-back"
                  onClick={goHome}
                  css={(theme: BirdiTheme) => css`
                    border: none;
                    background: transparent;
                    cursor: pointer;
                    color: ${theme.midnight};
                    display: flex;
                    padding: 4px;
                    border-radius: 6px;
                    flex-shrink: 0;
                    &:hover {
                      background: ${theme.mono10};
                    }
                    svg {
                      width: 20px;
                      height: 20px;
                    }
                  `}
                >
                  <ArrowLeft />
                </button>
                <span
                  css={css`
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                >
                  {(() => {
                    const t =
                      messages.find((m) => m.role === 'user')?.content ||
                      'Conversation';
                    return t.length > 42 ? `${t.slice(0, 41)}…` : t;
                  })()}
                </span>
              </div>
            ) : (
              <div
                css={(theme: BirdiTheme) => css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  font-weight: 700;
                  color: ${theme.midnight};
                  /* Keep the title on one line beside the Beta badge. It
                     wrapped to "Birdi's AI / Agent" once the ops model select
                     claimed its width; the smaller size buys room, and
                     flex-shrink:0 makes the select (below) give up width first
                     rather than the title. */
                  white-space: nowrap;
                  flex-shrink: 0;
                  font-size: 0.9em;
                `}
              >
                Birdi's AI Agent
                <BetaBadge />
              </div>
            )}
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 2px;
              `}
            >
              {opsModels.length > 0 && (
                <select
                  data-testid="assistant-ops-model"
                  aria-label="Model (ops only)"
                  title="Ops only — switch the model this chat runs on"
                  value={opsModel}
                  onChange={(e) => chooseOpsModel(e.target.value)}
                  css={(theme: BirdiTheme) => css`
                    max-width: 130px;
                    /* Yield width to the title before it wraps — this is the
                       ops-only debug control, so a clipped label here is a fair
                       trade for the brand name staying on one line. */
                    min-width: 0;
                    margin-right: 6px;
                    border: 1px solid ${theme.mono30};
                    border-radius: 6px;
                    background: ${theme.mono0};
                    color: ${theme.mono70};
                    font-family: inherit;
                    font-size: 0.72em;
                    padding: 2px 4px;
                    cursor: pointer;
                  `}
                >
                  <option value="">Default model</option>
                  {opsModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              )}
            {/* Standard icon button (midnight, no hover/tooltip) — same as
                the map inspector's close. */}
            <IconButton
              aria-label="Close assistant"
              data-testid="assistant-close"
              onClick={() => dispatch(closeAssistant())}
              css={css`
                /* Sized to the 16px header text rather than the 20px it was —
                   the glyph was taller than the cap height beside it and read
                   as oversized. Padding keeps the hit target comfortable. */
                padding: 4px;
                svg {
                  width: 16px;
                  height: 16px;
                }
              `}
            >
              <Close />
            </IconButton>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            css={css`
              flex: 1;
              overflow-y: auto;
              padding: 16px 14px;
              display: flex;
              flex-direction: column;
              gap: 14px;
            `}
          >
            {messages.length === 0 && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  gap: 14px;
                  padding: 0 8px ${recentChats.length > 0 ? '8px' : '40px'};
                  /* Fill the panel exactly: Scout, the prompt and the chips
                     stay pinned in frame; only the Recent-chats list below
                     scrolls. With no recents, centre the block as before. */
                  flex: 1;
                  min-height: 0;
                  justify-content: ${recentChats.length > 0
                    ? 'flex-start'
                    : 'center'};
                  /* On short screens the pinned block (avatar + prompt +
                     chips) eats the whole panel, collapsing the Recent-chats
                     scroller to nothing so it can't be reached at all. Below
                     this height, stop pinning and let the panel itself
                     scroll the lot. */
                  @media (max-height: 860px) {
                    flex: none;
                    justify-content: flex-start;
                  }
                `}
              >
                <span
                  aria-hidden
                  css={css`
                    display: flex;
                    flex-shrink: 0;
                    svg {
                      width: 72px;
                      height: 72px;
                      border-radius: 50%;
                    }
                  `}
                >
                  <ScoutAvatar />
                </span>
                <div
                  css={(theme: BirdiTheme) => css`
                    font-weight: 700;
                    color: ${theme.midnight};
                    font-size: 1.05em;
                    text-align: center;
                    line-height: 1.45;
                  `}
                >
                  {/* No beta badge here on purpose: inline in a bold centred
                      greeting the emoji + pill reads as clutter. The panel
                      header carries it, and it's visible at the same time. */}
                  Hi, I&apos;m Scout — Birdi&apos;s agent.
                  <br />
                  What would you like to do today?
                </div>
                <div
                  css={css`
                    position: relative;
                    width: 100%;
                  `}
                >
                  <textarea
                    data-testid="assistant-input"
                    ref={emptyStateInputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        onSubmit();
                      }
                    }}
                    placeholder="Ask anything or pick a suggestion below. E.g. 'I want to create a new map' or 'upload a new data set'"
                    rows={3}
                    autoFocus
                    css={(theme: BirdiTheme) => css`
                      display: block;
                      box-sizing: border-box;
                      width: 100%;
                      resize: none;
                      /* Height is driven by useAutoGrow (BIR-277) — grows to
                         8 rows, then scrolls. overflow-y is set inline by the
                         hook, so it isn't declared here. */
                      /* Sand at rest, canary on focus — same outline as the
                         dashboard hero and the follow-up composer. */
                      border: 1.5px solid ${theme.sand};
                      border-radius: 12px;
                      padding: 14px 40px 14px 14px;
                      font-family: inherit;
                      font-size: 0.95em;
                      line-height: 1.45;
                      color: ${theme.mono70};
                      outline: none;
                      &::placeholder {
                        color: ${theme.mono40};
                      }
                      &:focus {
                        border-color: ${theme.canary};
                      }
                    `}
                  />
                  <button
                    type="button"
                    aria-label="Send message"
                    onClick={onSubmit}
                    disabled={!input.trim()}
                    css={css`
                      position: absolute;
                      right: 10px;
                      bottom: 12px;
                      border: none;
                      background: transparent;
                      cursor: pointer;
                      display: flex;
                      padding: 2px;
                      &:disabled {
                        opacity: 0.45;
                        cursor: default;
                      }
                      svg {
                        width: 22px;
                        height: 22px;
                      }
                    `}
                  >
                    <SendGradient />
                  </button>
                  {contextChip && (
                    <div
                      css={css`
                        display: flex;
                        margin-top: 8px;
                      `}
                    >
                      <ContextChip label={contextChip} />
                    </div>
                  )}
                </div>
                <div
                  css={(theme: BirdiTheme) => css`
                    font-weight: 700;
                    color: ${theme.midnight};
                    font-size: 0.92em;
                    margin-top: 6px;
                  `}
                >
                  Try Birdi's AI Agent for...
                </div>
                <div
                  css={css`
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 10px;
                  `}
                >
                  {PANEL_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void send(s)}
                      css={(theme: BirdiTheme) => css`
                        border: none;
                        background: ${theme.lightOcean};
                        color: ${theme.midnight};
                        border-radius: 999px;
                        padding: 8px 15px;
                        font-size: 0.9em;
                        font-family: inherit;
                        cursor: pointer;
                        transition: all 0.15s ease;
                        &:hover {
                          color: ${theme.ocean};
                        }
                      `}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                {/* Recent chats — reopen a past server-side conversation */}
                {recentChats.length > 0 && (
                  <div
                    css={css`
                      width: 100%;
                      margin-top: 22px;
                      /* The home screen's one scrolling region — a long list
                         scrolls here instead of pushing Scout out of frame. */
                      flex: 1;
                      min-height: 0;
                      overflow-y: auto;
                      /* Matches the short-screen case above: the panel does
                         the scrolling, so this must not also clip. */
                      @media (max-height: 860px) {
                        flex: none;
                        overflow-y: visible;
                      }
                    `}
                  >
                    <div
                      css={(theme: BirdiTheme) => css`
                        font-weight: 700;
                        color: ${theme.midnight};
                        font-size: 0.92em;
                        margin-bottom: 6px;
                      `}
                    >
                      Recent chats
                    </div>
                    {(recentsExpanded
                      ? recentChats
                      : recentChats.slice(0, 6)
                    ).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid="assistant-recent-chat"
                        onClick={() => void openConversation(c.id)}
                        css={(theme: BirdiTheme) => css`
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: 10px;
                          width: 100%;
                          border: none;
                          background: transparent;
                          color: ${theme.midnight};
                          text-align: left;
                          font-family: inherit;
                          font-size: 0.95em;
                          padding: 10px 6px;
                          border-radius: 8px;
                          cursor: pointer;
                          &:hover {
                            background: ${theme.mono10};
                          }
                          svg {
                            width: 18px;
                            height: 18px;
                            flex-shrink: 0;
                          }
                          span {
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                          }
                        `}
                      >
                        <span>{c.title}</span>
                        <ArrowRight />
                      </button>
                    ))}
                    {!recentsExpanded && recentChats.length > 6 && (
                      <button
                        type="button"
                        data-testid="assistant-recents-more"
                        onClick={() => setRecentsExpanded(true)}
                        css={(theme: BirdiTheme) => css`
                          display: block;
                          width: 100%;
                          border: none;
                          background: transparent;
                          color: ${theme.ocean};
                          font-family: inherit;
                          font-size: 0.9em;
                          text-decoration: underline;
                          text-align: center;
                          padding: 10px 6px;
                          cursor: pointer;
                        `}
                      >
                        View more
                      </button>
                    )}
                  </div>
                )}

                {/* Same reminder as under the composer — the empty state has
                    its own centred input, so it needs its own copy. */}
                <div
                  css={css`
                    width: 100%;
                    margin-top: auto;
                    padding-top: 12px;
                  `}
                >
                  <AiDisclaimer />
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <MessageBubble
                key={m.id}
                message={m}
                suggestionsActive={
                  !streaming &&
                  i === messages.length - 1 &&
                  !m.selectedSuggestion
                }
                onSuggestion={(label) => chooseSuggestion(m.id, label)}
              />
            ))}

            {streaming &&
              messages.length > 0 &&
              messages[messages.length - 1].content === '' && (
                <div
                  css={(theme: BirdiTheme) => css`
                    color: ${theme.mono50};
                    font-size: 0.9em;
                    animation: ${pulse} 1.2s ease-in-out infinite;
                  `}
                >
                  Thinking…
                </div>
              )}

          </div>

          {/* Composer — the empty state renders its own centred input */}
          {messages.length > 0 && (
          <div
            css={(theme: BirdiTheme) => css`
              border-top: 1px solid ${theme.mono20};
              padding: 10px 12px;
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <textarea
              data-testid="assistant-input"
              ref={followUpInputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              placeholder="Ask a follow up question…"
              rows={1}
              autoFocus
              css={(theme: BirdiTheme) => css`
                width: 100%;
                box-sizing: border-box;
                resize: none;
                /* Height is driven by useAutoGrow (BIR-277). The old fixed
                   max-height: 120px never applied — nothing grew the box past
                   its one row — and 8 rows of this line-height is taller. */
                /* Sand at rest, canary on focus — matches the hero and the
                   empty-state composer. */
                border: 1px solid ${theme.sand};
                border-radius: 10px;
                padding: 10px 12px;
                font-family: inherit;
                font-size: 0.95em;
                line-height: 1.4;
                outline: none;
                &:focus {
                  border-color: ${theme.canary};
                }
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
              `}
            >
              {contextChip ? <ContextChip label={contextChip} /> : <span />}
            <button
              type="button"
              aria-label={streaming ? 'Stop response' : 'Send message'}
              title={streaming ? 'Stop' : 'Send'}
              data-testid="assistant-send"
              onClick={streaming ? () => abortRef.current?.abort() : onSubmit}
              disabled={!streaming && !input.trim()}
              css={(theme: BirdiTheme) => css`
                border: none;
                cursor: pointer;
                /* 32px keeps a comfortable hit target without the filled
                   stop chip reading as a heavy block next to 16px UI text —
                   the 40px version dominated the composer. */
                width: 32px;
                height: 32px;
                padding: 0;
                flex-shrink: 0;
                border-radius: 8px;
                /* Stop keeps the solid square; send is the bare gradient plane. */
                background: ${streaming ? theme.midnight : 'transparent'};
                color: ${theme.mono0};
                display: flex;
                align-items: center;
                justify-content: center;
                &:disabled {
                  opacity: 0.4;
                  cursor: default;
                }
                svg {
                  width: ${streaming ? '14px' : '22px'};
                  height: ${streaming ? '14px' : '22px'};
                }
              `}
            >
              {streaming ? (
                <svg viewBox="0 0 24 24" aria-hidden focusable="false">
                  <rect
                    x="7"
                    y="7"
                    width="10"
                    height="10"
                    rx="2"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <SendGradient />
              )}
            </button>
            </div>
            <AiDisclaimer />
          </div>
          )}
        </div>
      )}
    </>
  );
};

const MessageBubble: React.FC<{
  message: ChatMessage;
  /** Whether this message's quick replies are still clickable. */
  suggestionsActive: boolean;
  onSuggestion: (label: string) => void;
}> = ({ message, suggestionsActive, onSuggestion }) => {
  const isUser = message.role === 'user';
  return (
    <div
      data-testid={isUser ? 'assistant-message-user' : 'assistant-message'}
      css={css`
        display: flex;
        flex-direction: column;
        align-items: ${isUser ? 'flex-end' : 'flex-start'};
      `}
    >
      {message.tools.length > 0 && (
        <div
          css={(theme: BirdiTheme) => css`
            font-size: 0.78em;
            color: ${theme.mono50};
            margin-bottom: 4px;
          `}
        >
          {message.tools.map((t, i) => (
            <div key={i}>· {t}</div>
          ))}
        </div>
      )}
      {message.content && (
        <div
          css={(theme: BirdiTheme) => css`
            max-width: 90%;
            padding: 10px 12px;
            border-radius: 12px;
            font-size: 0.95em;
            line-height: 1.5;
            word-break: break-word;
            background: ${isUser ? theme.lightOcean : theme.mono10};
            color: ${isUser ? theme.midnight : theme.mono70};
            ${isUser ? 'white-space: pre-wrap;' : ''}
          `}
        >
          {isUser ? message.content : <Markdown content={message.content} />}
        </div>
      )}
      {/* Quick replies stay in the transcript: clickable while this is the
          latest answer, then greyed out with the picked one highlighted. */}
      {!isUser && (message.suggestions?.length ?? 0) > 0 && (
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 8px;
          `}
        >
          {message.suggestions!.map((s) => {
            const selected = message.selectedSuggestion === s;
            return (
              <button
                key={s}
                type="button"
                data-testid="assistant-suggestion"
                disabled={!suggestionsActive}
                onClick={() => onSuggestion(s)}
                css={(theme: BirdiTheme) => css`
                  border: 1px solid
                    ${suggestionsActive ? theme.mono30 : theme.mono20};
                  background: ${selected ? theme.mono20 : theme.mono10};
                  color: ${suggestionsActive || selected
                    ? theme.midnight
                    : theme.mono40};
                  ${selected ? 'font-weight: 600;' : ''}
                  border-radius: 999px;
                  padding: 6px 14px;
                  font-size: 0.88em;
                  font-family: inherit;
                  cursor: ${suggestionsActive ? 'pointer' : 'default'};
                  transition: all 0.15s ease;
                  ${suggestionsActive
                    ? `&:hover {
                         border-color: ${theme.ocean};
                         color: ${theme.ocean};
                       }`
                    : ''}
                `}
              >
                {s}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
